import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import apiConfig from '../../../constants/apiConfig';
import { filesOrFoldersKeys, FileScopeType } from './useGetAllFiles';

// Selector helper
const selectWorkspaceId = (state: any) =>
  state.prepare?.workspace?.selectedWorkspace;

export type FolderPayload = {
  name: string;
  workspaceId: string;
  scopeType?: FileScopeType; // 'organization' for org-library folders
  /** Roster position seat name this folder is tagged to. Null clears the tag. */
  positionName?: string | null;
  /** Roster template the position name came from. */
  positionTemplateSlug?: string | null;
};

export type UpdateFolderPayload = Partial<FolderPayload> & {
  folderId: string;
  workspaceId: string;
};

export type DeleteFolderPayload = {
  folderId: string;
  workspaceId?: string;
};

export type FolderResponse = any;

export function useCreateFolderMutation(
  onSuccess?: (folder: FolderResponse) => void
): UseMutationResult<FolderResponse, unknown, FolderPayload> {
  return useMutation({
    mutationFn: async (payload: FolderPayload) => {
      const response = await axios.post(
        apiConfig.apiGateway.foldersBase,
        payload
      );
      return response.data;
    },
    onSuccess: (folder) => {
      toast.success('Folder created successfully');
      if (onSuccess) onSuccess(folder);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.response?.status?.msg ||
          'Error'
      );
    },
  });
}

export function useUpdateFolderMutation(
  onSuccess?: (folder: FolderResponse) => void
): UseMutationResult<FolderResponse, unknown, UpdateFolderPayload> {
  return useMutation({
    mutationFn: async (payload: UpdateFolderPayload) => {
      const { folderId, ...rest } = payload;
      const response = await axios.patch(
        `${apiConfig.apiGateway.foldersBase}/${folderId}`,
        rest
      );
      return response.data;
    },
    onSuccess: (folder) => {
      toast.success('Folder updated successfully');
      if (onSuccess) onSuccess(folder);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.response?.status?.msg ||
          'Error'
      );
    },
  });
}

export const useDeleteFolder = (
  onSuccess?: (folder: FolderResponse) => void
) => {
  const queryClient = useQueryClient();
  // TODO (aeortic): This function is now being used outside of the
  // exercise workspace context. So this selector is no longer a sufficient fall-back.
  const reduxWorkspaceId = useSelector(selectWorkspaceId);

  return useMutation({
    mutationFn: async (payload: DeleteFolderPayload) => {
      await axios.delete(
        `${apiConfig.apiGateway.foldersBase}/${payload.folderId}`
      );
    },
    onMutate: async (payload) => {
      const { folderId, workspaceId: payloadWorkspaceId } = payload;
      const workspaceId = payloadWorkspaceId || reduxWorkspaceId;

      if (!workspaceId) return {};

      const queryKey = filesOrFoldersKeys.allFiles(workspaceId);

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey, exact: false });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically remove folder and its children from cache
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.dataset) return old;
        return {
          ...old,
          dataset: old.dataset.filter(
            (item: any) =>
              item.id !== folderId && item.notefolderId !== folderId
          ),
        };
      });

      return { previousData, workspaceId };
    },
    onSuccess: (data) => {
      toast.success('Folder deleted successfully');
      if (onSuccess) onSuccess(data);
    },
    onError: (error: any, variables, context: any) => {
      // Rollback on error
      if (context?.previousData && context?.workspaceId) {
        queryClient.setQueryData(
          filesOrFoldersKeys.allFiles(context.workspaceId),
          context.previousData
        );
      }
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.response?.status?.msg ||
          'Failed to delete folder'
      );
    },
  });
};
